import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Truck } from 'lucide-react';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useCurrency } from '../../../../../context/CurrencyContext';
import { formatOrderReference } from '../../../../../utils/formatOrderReference';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

const inputClass =
  'h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 shadow-sm outline-none transition-colors ring-brand-500/20 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30';
const btnPrimaryClass =
  'inline-flex min-h-11 items-center justify-center rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-brand-600/20 transition hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50';
const btnSecondaryClass =
  'inline-flex min-h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50';
const sectionCardClass =
  'rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-900/[0.04] sm:p-6';
const kpiCardClass =
  'relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white via-white to-slate-50/80 p-5 shadow-md ring-1 ring-slate-900/[0.05] sm:p-6';
const fieldLabelClass = 'mb-1.5 block text-sm font-medium text-slate-800';
const panelInactiveClass = 'rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-5 opacity-55 sm:p-6';
const panelActiveClass =
  'rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-900/[0.04] sm:p-6';

type DeliveryActualCost = {
  id: string;
  currency: string;
  amountMinor: number;
  returnToStoreFeeMinor: number;
  isReshipLoss: boolean;
  carrier: string | null;
  invoiceRef: string | null;
  allocationStrategy: string;
  deliveryBatchId: string | null;
  salesOrderId: string | null;
  createdAt: string;
  status: 'TEMPORARY' | 'CONFIRMED';
  originalAmountMinor: number | null;
  isAdjustedOnConfirm: boolean;
  confirmedAt: string | null;
  confirmationNote: string | null;
  confirmedBy?: { id: string; email: string } | null;
};

type BatchListItem = {
  id: string;
  name: string | null;
  areaLabel: string | null;
  country: string | null;
  cityKey: string | null;
  windowStart: string;
  windowEnd: string;
  status: string;
  orderCount: number;
  deliverySpeed: string;
};

type OrderListItem = {
  id: string;
  status: string;
  createdAt: string;
  totalUsd: unknown;
  currency: string;
  customer?: {
    email: string;
    customerProfile?: { fullName?: string | null; phone?: string | null } | null;
  } | null;
  addressSnapshot?: { city?: string; country?: string } | null;
};

function useDebounced<T>(value: T, ms: number): T {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

function formatBatchSummary(b: BatchListItem) {
  const windowStr = `${new Date(b.windowStart).toLocaleDateString()} → ${new Date(b.windowEnd).toLocaleDateString()}`;
  const area = [b.areaLabel, b.cityKey, b.country].filter(Boolean).join(' · ') || '—';
  return `${b.name || 'Batch'} · ${area} · ${b.status} · ${b.orderCount} order(s) · ${windowStr} · ${b.deliverySpeed}`;
}

function formatOrderSummary(o: OrderListItem) {
  const name = o.customer?.customerProfile?.fullName?.trim() || '—';
  const email = o.customer?.email || '—';
  const total = Number(o.totalUsd ?? 0).toFixed(2);
  const city = o.addressSnapshot?.city || '—';
  const when = new Date(o.createdAt).toLocaleString();
  return `${formatOrderReference(o.id)} · ${name} · ${email} · ${o.status} · ${o.currency} ${total} · ${city} · ${when}`;
}

export default function DeliveryCostsPage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const { formatPrice, formatPriceFrom, formatMajorInCurrency, supportedCurrencies, currency: userCurrency } =
    useCurrency();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<DeliveryActualCost[]>([]);

  const [showRecordForm, setShowRecordForm] = useState(false);
  /** What this cost is tied to: batch (split) vs single order (full amount). */
  const [targetKind, setTargetKind] = useState<'batch' | 'order'>('batch');

  const [currency, setCurrency] = useState('USD');
  const [amountMajor, setAmountMajor] = useState('');
  const [returnToStoreFeeMajor, setReturnToStoreFeeMajor] = useState('0');
  const [carrier, setCarrier] = useState('');
  const [allocationStrategy, setAllocationStrategy] = useState<'EQUAL_SPLIT' | 'WEIGHT_SHARE'>('EQUAL_SPLIT');

  const [batchSearch, setBatchSearch] = useState('');
  const debouncedBatchQ = useDebounced(batchSearch, 300);
  const [batchResults, setBatchResults] = useState<BatchListItem[]>([]);
  const [batchOpen, setBatchOpen] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<BatchListItem | null>(null);

  const [orderSearch, setOrderSearch] = useState('');
  const debouncedOrderQ = useDebounced(orderSearch, 300);
  const [orderResults, setOrderResults] = useState<OrderListItem[]>([]);
  const [orderOpen, setOrderOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<OrderListItem | null>(null);

  const [saving, setSaving] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const globalAuthorizedFetch = useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      // Delivery costs page is intentionally global (not department-scoped).
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );

  const currencyOptions = useMemo(() => {
    const list = supportedCurrencies.length
      ? [...supportedCurrencies].sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
      : [{ id: 'usd', code: 'USD', name: 'US Dollar', symbol: '$', sortOrder: 0 }];
    return list;
  }, [supportedCurrencies]);

  useEffect(() => {
    const codes = currencyOptions.map((c) => c.code.toUpperCase());
    const preferred = userCurrency.toUpperCase();
    if (codes.includes(preferred)) setCurrency(preferred);
    else if (codes[0]) setCurrency(codes[0]);
  }, [currencyOptions, userCurrency]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/finance/delivery-costs`);
      if (!res.ok) throw new Error('Failed');
      const data = (await res.json()) as DeliveryActualCost[];
      setRows(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }, [globalAuthorizedFetch]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (targetKind !== 'batch') {
      setBatchResults([]);
      return;
    }
    const q = debouncedBatchQ.trim();
    if (q.length < 2) {
      setBatchResults([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const url = new URL(`${API_BASE}/sales/delivery-batches`);
        url.searchParams.set('q', q);
        const res = await globalAuthorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as BatchListItem[];
        if (!cancelled) setBatchResults(Array.isArray(data) ? data.slice(0, 12) : []);
      } catch {
        if (!cancelled) setBatchResults([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedBatchQ, globalAuthorizedFetch, targetKind]);

  useEffect(() => {
    if (targetKind !== 'order') {
      setOrderResults([]);
      return;
    }
    const q = debouncedOrderQ.trim();
    if (q.length < 2) {
      setOrderResults([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const url = new URL(`${API_BASE}/sales/orders`);
        url.searchParams.set('q', q);
        const res = await globalAuthorizedFetch(url.toString());
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as OrderListItem[];
        if (!cancelled) {
          const filtered = Array.isArray(data)
            ? data.filter((o) => ['PAID', 'SHIPPED', 'RESHIPPED'].includes(String(o.status)))
            : [];
          setOrderResults(filtered.slice(0, 15));
        }
      } catch {
        if (!cancelled) setOrderResults([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedOrderQ, globalAuthorizedFetch, targetKind]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) {
        setBatchOpen(false);
        setOrderOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const totalMinor = rows.reduce(
    (sum, r) => sum + r.amountMinor + Number(r.returnToStoreFeeMinor || 0),
    0,
  );
  const batchCount = rows.filter((r) => !!r.deliveryBatchId).length;
  const orderCount = rows.filter((r) => !!r.salesOrderId).length;

  const resetForm = () => {
    setAmountMajor('');
    setReturnToStoreFeeMajor('0');
    setCarrier('');
    setAllocationStrategy('EQUAL_SPLIT');
    setBatchSearch('');
    setSelectedBatch(null);
    setOrderSearch('');
    setSelectedOrder(null);
    setBatchOpen(false);
    setOrderOpen(false);
  };

  const onTargetKindChange = (kind: 'batch' | 'order') => {
    setTargetKind(kind);
    setBatchSearch('');
    setSelectedBatch(null);
    setOrderSearch('');
    setSelectedOrder(null);
    setBatchOpen(false);
    setOrderOpen(false);
    setBatchResults([]);
    setOrderResults([]);
  };

  const onSave = async () => {
    const major = Number(String(amountMajor).replace(',', '.'));
    if (!Number.isFinite(major) || major <= 0) {
      setError(t('backoffice.delivery_costs_amount_invalid', 'Enter a valid amount greater than zero.'));
      return;
    }
    const amountMinor = Math.round(major * 100);
    const returnFeeMajor = Number(String(returnToStoreFeeMajor || '0').replace(',', '.'));
    if (!Number.isFinite(returnFeeMajor) || returnFeeMajor < 0) {
      setError('Return-to-store fee must be zero or higher.');
      return;
    }
    const returnToStoreFeeMinor = Math.round(returnFeeMajor * 100);
    const deliveryBatchId = targetKind === 'batch' ? selectedBatch?.id : undefined;
    const salesOrderId = targetKind === 'order' ? selectedOrder?.id : undefined;
    if (targetKind === 'batch' && !deliveryBatchId) {
      setError(t('backoffice.delivery_costs_pick_batch', 'Search and select a delivery batch.'));
      return;
    }
    if (targetKind === 'order' && !salesOrderId) {
      setError(t('backoffice.delivery_costs_pick_order', 'Search and select an order.'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/finance/delivery-costs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currency,
          amountMinor,
          returnToStoreFeeMinor,
          isReshipLoss: returnToStoreFeeMinor > 0,
          carrier: carrier.trim() ? carrier.trim() : undefined,
          allocationStrategy,
          deliveryBatchId,
          salesOrderId,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any)?.message || 'Save failed');
      }
      resetForm();
      setShowRecordForm(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const allocationDisabled = targetKind === 'order';
  const batchDisabled = targetKind === 'order';
  const orderDisabled = targetKind === 'batch';

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/finance/delivery-costs">
      <main className="workspace-page">
        <section>
          <div className="mb-3 flex items-center gap-3 text-brand-600">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 ring-1 ring-brand-100">
              <Truck className="h-5 w-5" strokeWidth={2} aria-hidden />
            </span>
            <div>
              <h1 className="workspace-page-title">
                {t('backoffice.delivery_costs_title', 'Delivery costs')}
              </h1>
              <p className="mt-1 max-w-3xl workspace-page-subtitle text-slate-600">
                {t(
                  'backoffice.delivery_costs_subtitle',
                  'Record what you really paid carriers or couriers. Link a cost to a whole delivery batch (split across orders) or to a single order.',
                )}
              </p>
            </div>
          </div>
        </section>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-medium text-red-800 shadow-sm">
            {error}
          </div>
        ) : null}

        <section className="workspace-panel">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <div className={`${kpiCardClass} border-t-4 border-t-brand-500`}>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('backoffice.delivery_costs_tracked', 'Tracked delivery cost')}
              </p>
              <p className="mt-3 text-3xl font-bold tabular-nums tracking-tight text-slate-900 sm:text-4xl">
                <span className="text-lg font-semibold text-slate-500 sm:text-xl">{userCurrency}</span>{' '}
                {formatPrice(totalMinor / 100)}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-slate-600">
                {t('backoffice.delivery_costs_kpi_total_hint', 'Sum of recorded actual costs in this list.')}
              </p>
            </div>
            <div className={`${kpiCardClass} border-t-4 border-t-amber-500`}>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('backoffice.delivery_costs_batch_alloc', 'Batch-linked entries')}
              </p>
              <p className="mt-3 text-3xl font-bold tabular-nums text-slate-900 sm:text-4xl">{batchCount}</p>
              <p className="mt-2 text-xs text-slate-600">
                {t('backoffice.delivery_costs_kpi_batch_hint', 'Costs allocated across a delivery batch.')}
              </p>
            </div>
            <div className={`${kpiCardClass} border-t-4 border-t-emerald-500`}>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('backoffice.delivery_costs_order_alloc', 'Single-order entries')}
              </p>
              <p className="mt-3 text-3xl font-bold tabular-nums text-slate-900 sm:text-4xl">{orderCount}</p>
              <p className="mt-2 text-xs text-slate-600">
                {t('backoffice.delivery_costs_kpi_order_hint', 'Costs tied to one order only.')}
              </p>
            </div>
          </div>
        </section>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <button
            type="button"
            className={showRecordForm ? btnSecondaryClass : `${btnPrimaryClass} min-w-[12rem]`}
            onClick={() => {
              setShowRecordForm((v) => !v);
              if (showRecordForm) resetForm();
            }}
          >
            {showRecordForm
              ? t('backoffice.delivery_costs_cancel_record', 'Cancel')
              : t('backoffice.delivery_costs_open_form', 'Record delivery fee')}
          </button>
          {showRecordForm ? (
            <p className="text-sm text-slate-600 sm:max-w-xl">
              {t('backoffice.delivery_costs_form_hint', 'Fill in amount, choose batch or order, then save.')}
            </p>
          ) : null}
        </div>

        {showRecordForm ? (
          <section
            ref={formRef}
            className="glass-panel space-y-8 rounded-2xl border border-slate-200/60 p-6 shadow-md ring-1 ring-slate-900/[0.04] sm:p-8"
          >
            <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200/80 pb-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-brand-600">
                  {t('backoffice.delivery_costs_form_badge', 'New entry')}
                </p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">
                  {t('backoffice.delivery_costs_form_title', 'New delivery cost')}
                </h2>
              </div>
            </div>

            <fieldset className={`${sectionCardClass} space-y-4 bg-slate-50/40`}>
              <legend className="text-base font-semibold text-slate-900">
                {t('backoffice.delivery_costs_apply_to', 'Apply this cost to')}
              </legend>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label
                  className={[
                    'flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition',
                    targetKind === 'batch'
                      ? 'border-brand-300 bg-brand-50/50 ring-2 ring-brand-200/60'
                      : 'border-slate-200 bg-white hover:border-slate-300',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="targetKind"
                    checked={targetKind === 'batch'}
                    onChange={() => onTargetKindChange('batch')}
                    className="mt-0.5 h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm font-medium leading-snug text-slate-800">
                    {t('backoffice.delivery_costs_kind_batch', 'A delivery batch (split across orders in that batch)')}
                  </span>
                </label>
                <label
                  className={[
                    'flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition',
                    targetKind === 'order'
                      ? 'border-brand-300 bg-brand-50/50 ring-2 ring-brand-200/60'
                      : 'border-slate-200 bg-white hover:border-slate-300',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="targetKind"
                    checked={targetKind === 'order'}
                    onChange={() => onTargetKindChange('order')}
                    className="mt-0.5 h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="text-sm font-medium leading-snug text-slate-800">
                    {t('backoffice.delivery_costs_kind_order', 'One order only (full amount on that order)')}
                  </span>
                </label>
              </div>
            </fieldset>

            <div className={`${sectionCardClass} grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3`}>
              <div>
                <label className={fieldLabelClass}>
                  {t('backoffice.delivery_costs_amount', 'Amount')}
                </label>
                <input
                  className={inputClass}
                  inputMode="decimal"
                  placeholder={t('backoffice.delivery_costs_amount_ph', 'e.g. 24.99') as string}
                  value={amountMajor}
                  onChange={(e) => setAmountMajor(e.target.value)}
                />
                <p className="mt-1 text-xs text-slate-600">
                  {t('backoffice.delivery_costs_amount_help', 'Enter the total in normal units (not cents).')}
                </p>
              </div>

              <div>
                <label className={fieldLabelClass}>
                  {t('backoffice.delivery_costs_currency', 'Currency')}
                </label>
                {currencyOptions.length <= 1 ? (
                  <div className={`${inputClass} flex items-center bg-slate-50 text-slate-700`}>{currency}</div>
                ) : (
                  <select className={inputClass} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                    {currencyOptions.map((c) => (
                      <option key={c.id} value={c.code.toUpperCase()}>
                        {c.code}
                        {c.name ? ` — ${c.name}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className={fieldLabelClass}>
                  {t('backoffice.delivery_costs_carrier', 'Carrier / service (optional)')}
                </label>
                <input
                  className={inputClass}
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  placeholder={t('backoffice.delivery_costs_carrier_ph', 'e.g. DHL, UPS') as string}
                />
              </div>
              <div>
                <label className={fieldLabelClass}>
                  Return-to-store fee (USD)
                </label>
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={returnToStoreFeeMajor}
                  onChange={(e) => setReturnToStoreFeeMajor(e.target.value)}
                  placeholder="0"
                />
                <p className="mt-1 text-xs text-slate-600">
                  Optional. Defaults to 0. Adds extra loss when courier returns goods.
                </p>
              </div>
            </div>

            <div className={batchDisabled ? panelInactiveClass : `${panelActiveClass} border-l-4 border-l-brand-500`}>
              <label className="text-base font-semibold text-slate-900">
                {t('backoffice.delivery_costs_batch_label', 'Delivery batch')}
              </label>
              <p className="mb-3 mt-1 text-sm text-slate-600">
                {t('backoffice.delivery_costs_batch_help', 'Type at least 2 characters to search by name, area, or notes.')}
              </p>
              <div className="relative">
                {!batchDisabled && selectedBatch ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1 rounded-xl border border-emerald-200/90 bg-emerald-50/70 px-4 py-3 text-sm leading-relaxed text-slate-800">
                      {formatBatchSummary(selectedBatch)}
                    </div>
                    <button
                      type="button"
                      className={`${btnSecondaryClass} !min-h-10 shrink-0 px-4 py-2`}
                      onClick={() => {
                        setSelectedBatch(null);
                        setBatchSearch('');
                        setBatchOpen(false);
                      }}
                    >
                      {t('backoffice.delivery_costs_change_selection', 'Change')}
                    </button>
                  </div>
                ) : (
                  <input
                    className={inputClass}
                    disabled={batchDisabled}
                    value={batchDisabled ? '' : batchSearch}
                    onChange={(e) => {
                      setBatchSearch(e.target.value);
                      setBatchOpen(true);
                    }}
                    onFocus={() => !batchDisabled && setBatchOpen(true)}
                    placeholder={
                      batchDisabled
                        ? (t('backoffice.delivery_costs_disabled_order_mode', 'Switch to “batch” above to use this.') as string)
                        : (t('backoffice.delivery_costs_batch_ph', 'Search batches…') as string)
                    }
                  />
                )}
                {batchOpen && !batchDisabled && !selectedBatch && batchResults.length > 0 ? (
                  <ul
                    className="absolute z-20 mt-2 max-h-64 w-full overflow-auto rounded-xl border border-slate-200/90 bg-white py-1.5 shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5"
                    role="listbox"
                  >
                    {batchResults.map((b) => (
                      <li key={b.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={false}
                          className="w-full px-4 py-2.5 text-left text-sm text-slate-800 transition hover:bg-brand-50/90"
                          onClick={() => {
                            setSelectedBatch(b);
                            setBatchSearch('');
                            setBatchOpen(false);
                          }}
                        >
                          {formatBatchSummary(b)}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>

            <div className={orderDisabled ? panelInactiveClass : `${panelActiveClass} border-l-4 border-l-emerald-500`}>
              <label className="text-base font-semibold text-slate-900">
                {t('backoffice.delivery_costs_order_label', 'Order')}
              </label>
              <p className="mb-3 mt-1 text-sm text-slate-600">
                {t(
                  'backoffice.delivery_costs_order_help',
                  'Search by order id, customer email, name, or phone (at least 2 characters).',
                )}
              </p>
              <div className="relative">
                {!orderDisabled && selectedOrder ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="min-w-0 flex-1 rounded-xl border border-emerald-200/90 bg-emerald-50/70 px-4 py-3 text-sm leading-relaxed text-slate-800 break-words">
                      {formatOrderSummary(selectedOrder)}
                    </div>
                    <button
                      type="button"
                      className={`${btnSecondaryClass} !min-h-10 shrink-0 px-4 py-2`}
                      onClick={() => {
                        setSelectedOrder(null);
                        setOrderSearch('');
                        setOrderOpen(false);
                      }}
                    >
                      {t('backoffice.delivery_costs_change_selection', 'Change')}
                    </button>
                  </div>
                ) : (
                  <input
                    className={inputClass}
                    disabled={orderDisabled}
                    value={orderDisabled ? '' : orderSearch}
                    onChange={(e) => {
                      setOrderSearch(e.target.value);
                      setOrderOpen(true);
                    }}
                    onFocus={() => !orderDisabled && setOrderOpen(true)}
                    placeholder={
                      orderDisabled
                        ? (t('backoffice.delivery_costs_disabled_batch_mode', 'Switch to “one order” above to use this.') as string)
                        : (t('backoffice.delivery_costs_order_ph', 'Search orders…') as string)
                    }
                  />
                )}
                {orderOpen && !orderDisabled && !selectedOrder && orderResults.length > 0 ? (
                  <ul
                    className="absolute z-20 mt-2 max-h-72 w-full overflow-auto rounded-xl border border-slate-200/90 bg-white py-1.5 shadow-xl shadow-slate-900/10 ring-1 ring-slate-900/5"
                    role="listbox"
                  >
                    {orderResults.map((o) => (
                      <li key={o.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={false}
                          className="w-full px-4 py-2.5 text-left text-sm text-slate-800 transition hover:bg-brand-50/90"
                          onClick={() => {
                            setSelectedOrder(o);
                            setOrderSearch('');
                            setOrderOpen(false);
                          }}
                        >
                          {formatOrderSummary(o)}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>

            <div className={allocationDisabled ? panelInactiveClass : `${panelActiveClass} border-l-4 border-l-amber-500`}>
              <label className="text-base font-semibold text-slate-900">
                {t('backoffice.delivery_costs_split_label', 'How to split across orders in the batch')}
              </label>
              <p className="mb-3 mt-1 text-sm text-slate-600">
                {allocationDisabled
                  ? t('backoffice.delivery_costs_split_na', 'Not used when you link to a single order.')
                  : t(
                      'backoffice.delivery_costs_split_help',
                      'Choose how this total is divided for profit reporting when the batch has several orders.',
                    )}
              </p>
              <select
                className={inputClass}
                disabled={allocationDisabled}
                value={allocationStrategy}
                onChange={(e) => setAllocationStrategy(e.target.value as 'EQUAL_SPLIT' | 'WEIGHT_SHARE')}
              >
                <option value="EQUAL_SPLIT">
                  {t('backoffice.delivery_costs_split_equal', 'Same share for each order (equal split)')}
                </option>
                <option value="WEIGHT_SHARE">
                  {t('backoffice.delivery_costs_split_weight', 'By order size (larger orders pay more of the cost)')}
                </option>
              </select>
            </div>

            <div className="sticky bottom-4 z-10 flex flex-wrap gap-3 rounded-2xl border border-slate-200/90 bg-white/95 p-4 shadow-lg shadow-slate-900/5 backdrop-blur-md supports-[backdrop-filter]:bg-white/85">
              <button type="button" className={btnPrimaryClass} onClick={onSave} disabled={saving}>
                {saving ? t('common.saving', 'Saving…') : t('backoffice.delivery_costs_save', 'Save delivery cost')}
              </button>
              <button
                type="button"
                className={btnSecondaryClass}
                onClick={() => {
                  resetForm();
                  setShowRecordForm(false);
                }}
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </section>
        ) : null}

        <section className="glass-panel space-y-5 p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
                {t('backoffice.delivery_costs_recent', 'Recent delivery costs')}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                {t('backoffice.delivery_costs_recent_hint', 'Latest records feed profit reports as delivery actuals.')}
              </p>
            </div>
            <button
              type="button"
              className={`${btnSecondaryClass} inline-flex items-center gap-2`}
              onClick={load}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
              {t('common.refresh', 'Refresh')}
            </button>
          </div>
          {loading ? (
            <p className="text-sm font-medium text-slate-600">{t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-12 text-center">
              <p className="text-sm font-medium text-slate-700">
                {t('backoffice.delivery_costs_empty', 'No delivery costs recorded yet.')}
              </p>
              <p className="mt-2 text-xs text-slate-500">
                {t('backoffice.delivery_costs_empty_hint', 'Use “Record delivery fee” above to add your first cost.')}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
              <table className="w-full min-w-[42rem] table-auto text-left text-sm">
                <thead className="border-b border-slate-200 bg-slate-100/90 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-3.5">{t('backoffice.delivery_costs_col_created', 'Created')}</th>
                    <th className="whitespace-nowrap px-4 py-3.5">{t('backoffice.delivery_costs_col_amount', 'Amount')}</th>
                    <th className="whitespace-nowrap px-4 py-3.5">Return fee</th>
                    <th className="whitespace-nowrap px-4 py-3.5">{t('backoffice.delivery_costs_col_carrier', 'Carrier')}</th>
                    <th className="whitespace-nowrap px-4 py-3.5">{t('backoffice.delivery_costs_col_split', 'Split')}</th>
                    <th className="whitespace-nowrap px-4 py-3.5">{t('backoffice.delivery_costs_col_batch', 'Batch')}</th>
                    <th className="whitespace-nowrap px-4 py-3.5">{t('backoffice.delivery_costs_col_order', 'Order')}</th>
                    <th className="whitespace-nowrap px-4 py-3.5">{t('common.status', 'Status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.id} className="transition hover:bg-brand-50/30">
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-600">
                        {new Date(r.createdAt).toLocaleString()}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-bold tabular-nums text-slate-900">
                        <div>{formatMajorInCurrency(r.amountMinor / 100, r.currency)}</div>
                        <div className="text-[11px] font-medium text-slate-500">
                          {formatPriceFrom(r.amountMinor / 100, (r.currency || 'USD').toUpperCase())}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-rose-700">
                        {(r.returnToStoreFeeMinor ?? 0) > 0
                          ? formatMajorInCurrency((r.returnToStoreFeeMinor ?? 0) / 100, r.currency)
                          : '—'}
                        {r.isReshipLoss ? (
                          <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-rose-600">
                            Reship loss
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{r.carrier || '—'}</td>
                      <td className="px-4 py-3 text-xs font-medium text-slate-600">
                        {r.deliveryBatchId
                          ? r.allocationStrategy === 'WEIGHT_SHARE'
                            ? t('backoffice.delivery_costs_split_weight_short', 'By order total')
                            : t('backoffice.delivery_costs_split_equal_short', 'Equal')
                          : '—'}
                      </td>
                      <td
                        className="max-w-[11rem] truncate px-4 py-3 font-mono text-xs text-slate-600"
                        title={r.deliveryBatchId ?? undefined}
                      >
                        {r.deliveryBatchId || '—'}
                      </td>
                      <td
                        className="max-w-[11rem] truncate px-4 py-3 font-mono text-xs text-slate-600"
                        title={r.salesOrderId ?? undefined}
                      >
                        {r.salesOrderId || '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-700">
                        <span
                          className={[
                            'rounded-full px-2 py-0.5 font-semibold',
                            r.status === 'CONFIRMED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800',
                          ].join(' ')}
                        >
                          {r.status}
                        </span>
                        {r.confirmedAt ? (
                          <div className="mt-1 text-[10px] text-slate-500">
                            {new Date(r.confirmedAt).toLocaleString()}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </MainLayout>
  );
}
